/**
 * usePagePermissions Hook
 *
 * Universal permission check hook for all pages.
 * Replaces scattered permission logic with centralized checks.
 *
 * Features:
 * - Reads from Zustand store (loaded by AuthWrapper/AuthGuard)
 * - localStorage caching for cross-app navigation (10-minute TTL)
 * - Eliminates redundant Firestore reads
 *
 * Usage:
 *   const { hasAccess, canEdit, availableLocations, selectedLocation } = usePagePermissions('rentals');
 */

import { useState, useEffect, useMemo } from 'react';
import { useFirebaseStore } from '../store/firebaseStore';
import { getPageConfig } from '../config/pages.config';
import { useLocations, useDefaultLocationId } from '../store/locationsStore';
import { useSelectedDivisionId, useDivisions } from '../store/divisionsStore';
import type {
  PagePermissionsResult,
  UserPermissions,
  PageAccessConfig,
} from '../types/permissions.types';
import { grantedLocationIds, holdsManagerFlag, isLocationBasedAccess, isRoleBasedAccess } from '../types/permissions.types';
import { logger } from '../utils/logger';

// ============================================================================
// Permission Caching (localStorage)
// ============================================================================
const PERMISSION_CACHE_KEY = 'user_permissions_cache';
const PERMISSION_CACHE_TTL = 1800000; // 30 minutes in milliseconds (Phase 1 optimization)

interface PermissionCacheEntry {
  email: string;
  data: UserPermissions;
  timestamp: number;
}

/**
 * Load permissions from localStorage cache
 * Returns cached data if valid, null otherwise
 */
function loadPermissionsFromCache(email: string): UserPermissions | null {
  try {
    const cached = localStorage.getItem(PERMISSION_CACHE_KEY);
    if (!cached) return null;

    const entry: PermissionCacheEntry = JSON.parse(cached);

    // Validate cache
    const now = Date.now();
    const age = now - entry.timestamp;

    // Check if cache is for same user and not expired
    if (entry.email === email && age < PERMISSION_CACHE_TTL) {
      return entry.data;
    }

    return null;
  } catch (error) {
    logger.error('[usePagePermissions] Error loading cache:', error);
    return null;
  }
}

/**
 * Save permissions to localStorage cache
 */
function savePermissionsToCache(permissions: UserPermissions): void {
  try {
    const entry: PermissionCacheEntry = {
      email: permissions.email,
      data: permissions,
      timestamp: Date.now(),
    };
    localStorage.setItem(PERMISSION_CACHE_KEY, JSON.stringify(entry));
  } catch (error) {
    logger.error('[usePagePermissions] Error saving cache:', error);
  }
}

/**
 * Main permission hook - use this in all pages
 *
 * @param pageName - Page ID from pages.config.ts (e.g., 'rentals', 'directory')
 * @returns Permission state and location management
 */
export function usePagePermissions(pageName: string): PagePermissionsResult {
  const currentUser = useFirebaseStore((state) => state.currentUser);
  const selectedLocation = useFirebaseStore((state) => state.selectedLocation);
  const setSelectedLocation = useFirebaseStore((state) => state.setSelectedLocation);

  // Reactive location data from Firestore (updates when locations change in admin panel)
  const locations = useLocations();
  const defaultLocationId = useDefaultLocationId();
  const locationIds = useMemo(() => locations.map(l => l.id), [locations]);

  // Division data
  const selectedDivisionId = useSelectedDivisionId();
  const divisions = useDivisions();

  const [userPermissions, setUserPermissions] = useState<UserPermissions | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Get page configuration
  const pageConfig = getPageConfig(pageName);

  // Load user permissions with localStorage caching
  // 1. Check localStorage cache first (fastest - 0 Firestore reads on cache hit)
  // 2. If cache miss, use Zustand store (loaded by AuthWrapper/AuthGuard)
  // 3. Save to cache for next page navigation
  useEffect(() => {
    // Public pages (requiresAuth: false) don't need permission checks
    if (pageConfig && !pageConfig.requiresAuth) {
      setIsLoading(false);
      setUserPermissions(null); // No permissions needed for public pages
      return;
    }

    if (!currentUser) {
      setIsLoading(false);
      setUserPermissions(null);
      return;
    }

    // Try to load from localStorage cache first
    const cachedPermissions = loadPermissionsFromCache(currentUser.email);
    if (cachedPermissions) {
      // Cache hit - use immediately
      setUserPermissions(cachedPermissions);
      setIsLoading(false);
      return;
    }

    // Cache miss - build from Zustand store (already loaded by AuthWrapper/AuthGuard)
    const permissions: UserPermissions = {
      email: currentUser.email,
      role: currentUser.role,
      pageAccess: (currentUser.pagePermissions || {}) as PageAccessConfig,
      defaultLocation: currentUser.defaultLocation,
    };

    setUserPermissions(permissions);
    setIsLoading(false);

    // Save to cache for next time
    savePermissionsToCache(permissions);
  }, [currentUser, pageConfig]);

  // Compute permission results
  const result = useMemo((): PagePermissionsResult => {
    // ========================================================================
    // DIVISION COMPUTATION
    // ========================================================================
    // Compute available divisions for this user
    const activeDivisionIds = divisions.filter(d => d.isActive).map(d => d.id);
    const computedAvailableDivisions = currentUser?.role === 'admin'
      ? activeDivisionIds
      : (currentUser?.divisions || []).filter(id => activeDivisionIds.includes(id));

    // Resolve selected division: use selectedDivisionId if available, otherwise first.
    // When divisions haven't loaded yet (empty array), preserve the store value
    // (from localStorage) so the sync effect in PageContainer doesn't clear it.
    const computedSelectedDivision = divisions.length === 0
      ? selectedDivisionId
      : selectedDivisionId && computedAvailableDivisions.includes(selectedDivisionId)
        ? selectedDivisionId
        : computedAvailableDivisions.length > 0 ? computedAvailableDivisions[0] : null;

    // Get the active division's location IDs for filtering
    const activeDivision = divisions.find(d => d.id === computedSelectedDivision && d.isActive);
    const divisionLocationIds = activeDivision?.locationIds || [];

    // Public pages (requiresAuth: false) - grant immediate access
    if (pageConfig && !pageConfig.requiresAuth) {
      return {
        hasAccess: true,
        isAdmin: false,
        canEdit: false,
        availableLocations: [],
        selectedLocation: null,
        setSelectedLocation,
        isLoading: false,
        userPermissions: null,
        locationWasSwitched: false,
        switchedFromLocation: null,
        selectedDivision: null,
        availableDivisions: [],
      };
    }

    // Default values when loading or no data
    if (isLoading || !userPermissions || !pageConfig) {
      return {
        hasAccess: false,
        isAdmin: false,
        canEdit: false,
        availableLocations: [],
        selectedLocation: null,
        setSelectedLocation,
        isLoading: true,
        userPermissions: null,
        locationWasSwitched: false,
        switchedFromLocation: null,
        selectedDivision: null,
        availableDivisions: [],
      };
    }

    // Admins have full access to everything, and so do holders of the page's manager
    // flag (e.g. quiz managers on Quizzes), read live from the store rather than the cache
    const isAdmin = userPermissions.role === 'admin';
    if (isAdmin || holdsManagerFlag(pageConfig, currentUser)) {
      // Admin gets all locations for location-based pages, filtered by division
      const filteredLocationIds = computedSelectedDivision && divisionLocationIds.length > 0
        ? locationIds.filter(id => divisionLocationIds.includes(id))
        : locationIds;

      // For location-based pages, auto-switch if current location is outside division
      let adminSelectedLocation = selectedLocation || userPermissions.defaultLocation || defaultLocationId;
      let adminLocationWasSwitched = false;
      let adminSwitchedFromLocation: string | null = null;

      if (pageConfig.hasLocations && filteredLocationIds.length > 0 &&
          adminSelectedLocation && !filteredLocationIds.includes(adminSelectedLocation)) {
        adminLocationWasSwitched = true;
        adminSwitchedFromLocation = adminSelectedLocation;
        adminSelectedLocation =
          (userPermissions.defaultLocation && filteredLocationIds.includes(userPermissions.defaultLocation))
            ? userPermissions.defaultLocation
            : filteredLocationIds[0];
      }

      return {
        hasAccess: true,
        isAdmin,
        canEdit: true,
        availableLocations: pageConfig.hasLocations ? filteredLocationIds : [],
        selectedLocation: adminSelectedLocation,
        setSelectedLocation,
        isLoading: false,
        userPermissions,
        locationWasSwitched: adminLocationWasSwitched,
        switchedFromLocation: adminSwitchedFromLocation,
        selectedDivision: computedSelectedDivision,
        availableDivisions: computedAvailableDivisions,
      };
    }

    // ========================================================================
    // PAGE ACCESS RESOLUTION
    // ========================================================================
    // Permissions are stored in pageAccess (compiled from roles on save, or set directly in advanced mode)
    const pageAccess = userPermissions.pageAccess?.[pageName];

    // No access if page not in pageAccess or not enabled
    if (!pageAccess || !pageAccess.enabled) {
      return {
        hasAccess: false,
        isAdmin: false,
        canEdit: false,
        availableLocations: [],
        selectedLocation: null,
        setSelectedLocation,
        isLoading: false,
        userPermissions,
        locationWasSwitched: false,
        switchedFromLocation: null,
        selectedDivision: computedSelectedDivision,
        availableDivisions: computedAvailableDivisions,
      };
    }

    // Role-based page (no locations, but has editor/viewer role)
    if (!pageConfig.hasLocations && isRoleBasedAccess(pageAccess)) {
      const canEdit = pageAccess.role === 'editor';
      return {
        hasAccess: true,
        isAdmin: false,
        canEdit,
        availableLocations: [],
        selectedLocation: null,
        setSelectedLocation,
        isLoading: false,
        userPermissions,
        locationWasSwitched: false,
        switchedFromLocation: null,
        selectedDivision: computedSelectedDivision,
        availableDivisions: computedAvailableDivisions,
      };
    }

    // Simple page (no locations, no roles) - just enabled/disabled
    if (!pageConfig.hasLocations) {
      return {
        hasAccess: true,
        isAdmin: false,
        canEdit: false, // Simple pages don't have edit concept
        availableLocations: [],
        selectedLocation: null,
        setSelectedLocation,
        isLoading: false,
        userPermissions,
        locationWasSwitched: false,
        switchedFromLocation: null,
        selectedDivision: computedSelectedDivision,
        availableDivisions: computedAvailableDivisions,
      };
    }

    // Location-based page - check location access
    if (isLocationBasedAccess(pageAccess)) {
      const locations = pageAccess.locations || {};
      const rawAvailableLocationIds = grantedLocationIds(pageConfig, pageAccess);

      // Filter available locations by division
      const availableLocationIds = computedSelectedDivision && divisionLocationIds.length > 0
        ? rawAvailableLocationIds.filter(id => divisionLocationIds.includes(id))
        : rawAvailableLocationIds;

      // No locations granted = no access
      if (availableLocationIds.length === 0) {
        return {
          hasAccess: false,
          isAdmin: false,
          canEdit: false,
          availableLocations: [],
          selectedLocation: null,
          setSelectedLocation,
          isLoading: false,
          userPermissions,
          locationWasSwitched: false,
          switchedFromLocation: null,
          selectedDivision: computedSelectedDivision,
          availableDivisions: computedAvailableDivisions,
        };
      }

      // Determine current location and track if auto-switch occurs
      let currentLocation = selectedLocation;
      let locationWasSwitched = false;
      let switchedFromLocation: string | null = null;

      if (!currentLocation || !availableLocationIds.includes(currentLocation)) {
        // Track that we're switching away from an inaccessible location
        if (currentLocation && !availableLocationIds.includes(currentLocation)) {
          locationWasSwitched = true;
          switchedFromLocation = currentLocation;
        }
        // Default to user's default location or first available
        currentLocation =
          (userPermissions.defaultLocation && availableLocationIds.includes(userPermissions.defaultLocation))
            ? userPermissions.defaultLocation
            : availableLocationIds[0];
      }

      // Check if user can edit this location
      const locationPermission = locations[currentLocation];
      const canEdit = locationPermission?.role === 'editor';

      return {
        hasAccess: true,
        isAdmin: false,
        canEdit,
        availableLocations: availableLocationIds,
        selectedLocation: currentLocation,
        setSelectedLocation,
        isLoading: false,
        userPermissions,
        locationWasSwitched,
        switchedFromLocation,
        selectedDivision: computedSelectedDivision,
        availableDivisions: computedAvailableDivisions,
      };
    }

    // Fallback - should not reach here
    return {
      hasAccess: false,
      isAdmin: false,
      canEdit: false,
      availableLocations: [],
      selectedLocation: null,
      setSelectedLocation,
      isLoading: false,
      userPermissions,
      locationWasSwitched: false,
      switchedFromLocation: null,
      selectedDivision: computedSelectedDivision,
      availableDivisions: computedAvailableDivisions,
    };
  }, [isLoading, userPermissions, pageConfig, pageName, selectedLocation, setSelectedLocation, locationIds, defaultLocationId, selectedDivisionId, divisions, currentUser]);

  return result;
}

/**
 * Lightweight hook to just check if user has admin role
 * Use this when you only need to know if user is admin
 */
export function useIsAdmin(): boolean {
  const currentUser = useFirebaseStore((state) => state.currentUser);
  return currentUser?.role === 'admin';
}

/**
 * Quiz manager: builds and assigns quizzes and scenarios at every store. Admins always are;
 * everyone else needs the admin-set users/{email}.quizManager switch.
 */
export function useIsQuizManager(): boolean {
  return useFirebaseStore((state) => state.currentUser?.role === 'admin' || state.currentUser?.quizManager === true);
}


/**
 * Clear localStorage permission cache
 * Call this after updating user permissions to ensure immediate effect
 *
 * Usage:
 *   import { invalidatePermissionCache } from '@/shared';
 *
 *   // After updating permissions in Firestore
 *   invalidatePermissionCache();
 */
export function invalidatePermissionCache(): void {
  try {
    localStorage.removeItem(PERMISSION_CACHE_KEY);
  } catch (error) {
    logger.error('[usePagePermissions] Error clearing cache:', error);
  }
}
